"use client";

import { useState, useRef } from "react";
import styles from "./CarImageGalleryWrapper.module.scss";
import Button from "@components/atoms/Button";
import Image from "@components/atoms/Image";
import assetLoader from "@utils/internalImageLoder";
import WishlistButton from "@components/atoms/WishlistButton";
import WishlistLimitModal from "@components/molecules/WishlistLimitModal";
import WishlistToast from "@components/atoms/WishlistToast";
import { useHasBottomNav } from "@hooks/useHasBottomNav";
import { useWishlistActions } from "@hooks/useWishlistActions";

interface CarImageGalleryWrapperProps {
  children: React.ReactNode[];
  className?: string;
  // Wishlist functionality props
  slug?: string;
  brandName?: string;
  modelName?: string;
  isLoggedIn?: boolean;
  cityName?: string;
}

export const CarImageGalleryWrapper = ({
  children,
  className = "",
  slug,
  brandName,
  modelName,
  isLoggedIn = false,
  cityName
}: CarImageGalleryWrapperProps) => {
  const hasBottomNav = useHasBottomNav();
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Use the custom wishlist actions hook
  const {
    showLimitModal,
    pendingWishlistCar,
    showWishlist,
    car,
    toast,
    wishlistItems,
    handleWishlistToggle,
    handleUndo,
    handleViewWishlist,
    handleReplace,
    closeLimitModal,
    isCarWishlisted,
  } = useWishlistActions({
    slug,
    brandName,
    modelName,
    isLoggedIn,
  });

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === children?.length - 1;

  const next = () => {
    if (!isLast) setCurrentIndex((prev) => prev + 1);
  };

  const prev = () => {
    if (!isFirst) setCurrentIndex((prev) => prev - 1);
  };

  const goTo = (index: number) => setCurrentIndex(index);

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const distance = touchStartX.current - touchEndX.current;
      if (distance > 50) {
        // Swiped left
        next();
      } else if (distance < -50) {
        // Swiped right
        prev();
      }
    }
    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <>
      <div className={className}>
        {/* Mobile */}
        <div className={styles.mobileLayout}>
          <div
            className={`${styles.mobileOverlay} ${styles["mobileOverlay--top"]}`}
          />
          <div
            className={`${styles.mobileOverlay} ${styles["mobileOverlay--bottom"]}`}
          />
          <div
            className={styles.mobileImageSection}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {children?.[currentIndex]}
          </div>
          <div className={styles.mobileNavigation}>
            {children?.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={
                  i === currentIndex
                    ? `${styles.navBar} ${styles.navBarActive}`
                    : styles.navBar
                }
              />
            ))}
          </div>
          {/* Mobile Wishlist Button - show only on first image */}
          {showWishlist && car && (
            <WishlistButton
              isLoggedIn={isLoggedIn}
              isInWishlist={isCarWishlisted(car.id)}
              onWishlistClick={() =>
                handleWishlistToggle(brandName!, modelName!, car)
              }
              size='small'
              className={styles.wishlistButton}
              model_name={modelName}
              brand_name={brandName}
              cityName={cityName}
            />
          )}
        </div>

        {/* Desktop */}
        <div className={styles.desktopLayout}>
          <div className={styles.desktopImageSection}>
            {children?.[currentIndex]}
          </div>
          <div className={styles.desktopNavigation}>
            <span className={styles.imageCounter}>
              {`${currentIndex + 1}/${children?.length}`}
            </span>
            <Button
              onClick={prev}
              disabled={isFirst}
              className={styles.navButton}
              size='ds'
              aria-disabled={isFirst}
            >
              <Image
                src={`${assetLoader()}/icons/HeroSection/ArrowLeft.svg`}
                alt={"Left Arrow"}
                className={styles.navIcon}
                width={20}
                height={20}
                loading='lazy'
              />
            </Button>
            <Button
              onClick={next}
              disabled={isLast}
              className={styles.navButton}
              size='ds'
              aria-disabled={isLast}
            >
              <Image
                src={`${assetLoader()}/icons/HeroSection/ArrowRight.svg`}
                alt={"Right Arrow"}
                className={styles.navIcon}
                width={20}
                height={20}
                loading='lazy'
              />
            </Button>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      <WishlistToast
        isVisible={toast.show}
        isAdding={toast.isAdding}
        carMake={toast.carMake}
        carName={toast.carName}
        hasBottomNav={hasBottomNav}
        onViewWishlist={handleViewWishlist}
        onUndo={handleUndo}
      />

      {/* Wishlist limit modal */}
      {showLimitModal && pendingWishlistCar && (
        <WishlistLimitModal
          isOpen={showLimitModal}
          onClose={closeLimitModal}
          wishlistedCars={wishlistItems}
          onReplace={handleReplace}
          newCar={pendingWishlistCar}
        />
      )}
    </>
  );
};
